<script setup lang="ts">
import type { ProductContributorWithDetails } from "~/composables/useProductContributors"

const { contributors } = useProductContributors()

const isSingleContributor = computed(() => contributors.value && contributors.value.length === 1)
const shouldShowImage = computed(() => contributors.value && contributors.value.length <= 2)

const getInitials = ({ details }: ProductContributorWithDetails) => {
  const { firstname, lastname } = details
  const initials = `${firstname.charAt(0)}${lastname.charAt(0)}`
  return initials.toUpperCase()
}

const getImageClass = (i: number) => ({
  "z-10": i === 0,
  "translate-x-[65%]": i === 1,
})
</script>

<template>
	<section aria-labelledby="contributors-heading" class="flex gap-5 font-national2 text-dark">
		<h2 id="contributors-heading" class="sr-only">Bidragsytere</h2>
		<div
			v-if="shouldShowImage"
			class="md:flex relative h-[60px]"
			:class="{ 'w-[60px] flex': isSingleContributor, 'hidden': !isSingleContributor, 'min-w-[100px]': contributors?.length === 2 }"
			aria-hidden="true"
		>
			<template v-for="(contributor, i) in contributors" :key="i">
				<img
					v-if="contributor.details.image?.src"
					:src="contributor.details.image?.src"
					:alt="contributor.details.image?.alt"
					:class="getImageClass(i)"
					class="absolute w-[60px] h-[60px] rounded-full bg-lightest"
				/>
				<!-- Show initials if no image -->
				<div
					v-else
					class="absolute flex items-center justify-center border rounded-full w-[60px] h-[60px] bg-dark text-platform-paper border-dark"
					:class="getImageClass(i)"
				>
					{{ getInitials(contributor) }}
				</div>
			</template>
		</div>
		<ul class="text-base md:text-lg/6 list-none p-0 flex flex-col justify-center md:gap-2">
			<li v-for="(contributor) in contributors" :key="contributor.locationId">
				<p class="font-extrabold" :class="{ 'flex flex-col': isSingleContributor }">
					{{ contributor.details.firstname }} {{ contributor.details.lastname }}
					<span class="font-normal italic">
						{{ contributor.details.role }}
					</span>
				</p>
			</li>
		</ul>
	</section>
</template>
